/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { ApiClient } from '../types/api/ApiClient';
import { EntityId } from '../types/api/EntityId';
import { DatasetImport } from '../types/dataset/DatasetImport';
import { ImportDatasetRequestBody } from '../types/dataset-import/ImportDatasetRequestBody';
import { getApiEndpoint } from '../utils/getApiEndpoint';
import { apiClientFactory } from '../utils/http-client/apiClientFactory';
import { axiosInstanceFactory } from '../utils/http-client/axiosInstanceFactory';
import { axiosJwtTokenInterceptor, axiosUnauthorizedInterceptor } from '../utils/http-client/axiosInterceptors';

class DatasetImportApiServiceClass {
  private api: ApiClient;

  constructor() {
    this.api = apiClientFactory(
      axiosInstanceFactory(
        { baseURL: getApiEndpoint() },
        [axiosJwtTokenInterceptor],
        [[undefined, axiosUnauthorizedInterceptor]],
      ),
    );
  }

  fetchClusterDatasetImports = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) => {
    return this.api.get<DatasetImport[]>(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/dataset-import`,
    );
  };

  importClusterDataset = ({
    organizationId,
    projectId,
    clusterId,
    data,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
    data: ImportDatasetRequestBody;
  }) => {
    return this.api.post(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/dataset-import`,
      data,
    );
  };

  setClusterDatasetModifiable = ({
    organizationId,
    projectId,
    clusterId,
    datasetImportId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
    datasetImportId: EntityId;
  }) => {
    return this.api.post(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/dataset-import/${datasetImportId}/editable`,
      {},
    );
  };

  deleteClusterDatasetFromQueue = ({
    organizationId,
    projectId,
    clusterId,
    datasetImportId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
    datasetImportId: EntityId;
  }) => {
    return this.api.del(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/dataset-import/${datasetImportId}/abort`,
    );
  };

  deleteClusterDataset = ({
    organizationId,
    projectId,
    clusterId,
    datasetImportId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
    datasetImportId: EntityId;
  }) => {
    return this.api.del(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/dataset-import/${datasetImportId}`,
    );
  };
}

export const DatasetImportApiService = new DatasetImportApiServiceClass();
